/* eslint-disable max-depth */
import _ from 'lodash'
import i18n from '@/lang'
import {
  cookAutoClickCategory,
  cookColorConfigCategory,
  cookYdClickCategory,
  cookAutoEndTimeCategory,
  cookPlayAgainCategory,
  cookGlobalClickCategory,
  gameConfigCategoryResourceType,
  categoryConfig,
  isGlobalCategory,
  createFeGameConfigCategoryId,
  isFePresetCategory,
} from './category'
import useIdToFindResourceList from '@/views/creative/utils/use-id-to-find-resource-list.js'
import {
  isGamePlay,
  isElement,
  isElementStars,
  isGameConfigStars,
  isGameConfigWithoutRelate,
  isCommonTemplateCustomPage,
  isImageLangPack,
  isModelCategory,
  isGlobalConfigCategory,
  isBgColorCategory,
  isCocosModelCategory,
  isCocosModelAnimationCategory,
} from '@/views/creative/utils'
import { hasPermission } from '@/views/creative/template/auth.js'
import { language } from '@/common/storage'
import {
  assignDefaultAnimationToProperties,
  assignDefaultModelToProperties,
  getAllAnimationResources,
  getAllModelResource,
} from '@/views/creative/utils/model-animation-utils'
import { assignDefaultMaterialToProperties } from '@/views/creative/utils/model-material-utils'

function getCommonGameConfigCategoryName(category) {
  if (!category) {
    return ''
  }
  return language.get() === 'en' ? category.name_en : category.name
}

function getCommonGameConfigCategoryDesc(category) {
  if (!category) {
    return ''
  }
  return language.get() === 'en' ? category.desc_en : category.desc
}

/**
 * cookElementStarsCategory
 * @param {{elementCategory: any, stars: number[]}} param0
 */
function cookElementStarsCategory({ elementCategory, stars }) {
  const elementStarsCategory = {
    // 元素的收藏前端的 id 是 elementStarsCategory，取这个数是为了避免与后端冲突
    id: categoryConfig.elementStars.id,
    pid: elementCategory.id,
    prefix: categoryConfig.elementStars.prefix,
    name: i18n.t('playTurbo.gamePlay.stars.name'),
    sub_category: [],
    level: 2,
  }

  _.each(elementCategory.sub_category, (i) => {
    // 忽略多组合元素分类，避免重复收藏
    if (i.id === categoryConfig.multiCustomCategory.id) return
    _.each(i.sub_category, (j) => {
      if (stars.some((starId) => starId === j.id)) {
        // 修改收藏内容的 pid
        elementStarsCategory.sub_category.push({
          ...j,
          pid: categoryConfig.elementStars.id,
        })
      }
    })
  })
  return _.cloneDeep(elementStarsCategory)
}

function cookGameConfigStarsCategory({ gameConfigCategory, stars }) {
  const gameConfigStarsCategory = {
    // 玩法的收藏前端的 id 是 gameConfigStarsCategory，取这个数是为了避免与后端冲突
    id: categoryConfig.gameConfigStars.id,
    pid: 3, // 玩法的 id 是 gameDifficulty
    prefix: categoryConfig.gameConfigStars.prefix,
    name: i18n.t('playTurbo.gamePlay.stars.name'),
    sub_category: [],
    level: 2,
  }
  _.each(gameConfigCategory.sub_category, (i) => {
    _.each(i.sub_category, (j) => {
      if (stars.some((starId) => starId === j.id)) {
        // 修改收藏内容的 pid
        gameConfigStarsCategory.sub_category.push({
          ...j,
          pid: categoryConfig.gameConfigStars.id,
        })
      }
    })
  })
  return _.cloneDeep(gameConfigStarsCategory)
}

/**
 * dco_part_id：dco 坑位 id
 * 不是展示特效、dco_坑位 id 大于 > 0、资源库资源
 * @param category
 * @returns {boolean|boolean|*|boolean}
 */
const isNeedToAddTag = (category) => {
  return (
    category.dco_part_id > 0 &&
    category.isRepository &&
    category.res_cat2_id !== 2
  )
}

// 通用模板制作玩法分类
const commonTemplateCustomGameConfigCategory = [
  categoryConfig.autoClick.id,
  categoryConfig.globalClick.id,
  categoryConfig.ydClick.id,
]

/**
 * rebuildCategory
 * @param {{i18nResource: {},gameConfigResource: {},category: {isRepository: boolean, res_type: number, isNeedToAddTag: boolean, dco_part_id: number, res_cat2_id: number, sub_category: any, id: number, properties: any, hasGameConfigSub: boolean, resource_type: any, prefix: any}[], resource: {}, jsonConfig: {autoEndTime: number, playAgain: number, gameConfig: [], colorConfig: any}, page: string, commit?: function, playConfig: any, starsConfig: any, gameDifficulty: string, isMultiCustom: boolean, enableGameConfig: number}} param0
 */
export default function rebuildCategory({
  category,
  resource,
  jsonConfig,
  page,
  commit,
  playConfig,
  starsConfig,
  gameDifficulty,
  isMultiCustom,
  gameConfigResource,
  i18nResource,
  level = 1,
  mwConfig,
  supportedMwConfigs,
  enableGameConfig,
  permissionKeys,
  hasInitGameConfigCategory,
  hasInitPlayAgainCategory,
}) {
  const hasTargetGameConfig = (key) => {
    if (key === categoryConfig.playAgain.prefix) {
      return _.isNumber(jsonConfig.playAgain)
    }

    if (key === categoryConfig.autoEndTime.prefix) {
      return _.isNumber(jsonConfig.autoEndTime)
    }

    if (
      [
        categoryConfig.globalClick.prefix,
        categoryConfig.autoClick.prefix,
        categoryConfig.ydClick.prefix,
      ].some((i) => i === key)
    ) {
      return (
        supportedMwConfigs.some((item) => item === key) &&
        _.isNumber(_.get(mwConfig, key))
      )
    }

    if (key === categoryConfig.colorConfig.prefix) {
      return (
        _.isPlainObject(jsonConfig.colorConfig) &&
        !_.isEmpty(jsonConfig.colorConfig)
      )
    }

    return false
  }

  const newCategory = category.map((categoryItem) => {
    // 6音频库
    // 7手指库
    // 8背景图库
    // 9皮肤库
    // 10特效库
    // 11主角库
    // 是否资源库资源
    categoryItem.isRepository =
      [6, 7, 8, 9, 10, 11].indexOf(categoryItem.res_type) > -1
    // 是否需要添加 tag
    categoryItem.isNeedToAddTag = isNeedToAddTag(categoryItem)
    // 在分类结构中的层级
    categoryItem.level = level
    // 是否是 model 坑位
    categoryItem.isModel = isModelCategory(categoryItem.id, resource)
    categoryItem.isGlobalConfig = isGlobalConfigCategory(
      categoryItem.resource_type,
    )
    categoryItem.isBgColor = isBgColorCategory(categoryItem.prefix)

    // 分类属性
    if (
      categoryItem &&
      categoryItem.properties &&
      _.isString(categoryItem.properties)
    ) {
      try {
        categoryItem.properties = JSON.parse(categoryItem.properties)
      } catch (e) {
        console.error(e)
        categoryItem.properties = {}
      }
    } else {
      categoryItem.properties = categoryItem.properties || {}
    }
    if (
      categoryItem.isGlobalConfig &&
      categoryItem.isBgColor &&
      categoryItem.properties.globalConfig
    ) {
      const globalConfig = categoryItem.properties.globalConfig
      // 补充原始背景颜色
      if (
        _.get(globalConfig, 'value') &&
        !_.get(globalConfig, 'originalValue')
      ) {
        globalConfig.originalValue = globalConfig.value
      }
      categoryItem.properties = {
        ...categoryItem.properties,
        globalConfig,
      }
    }
    // 如果是模型的分类，需要填充动画
    if (isCocosModelCategory(categoryItem)) {
      assignDefaultAnimationToProperties({
        modelAnimationResource: getAllAnimationResources(resource),
        category: categoryItem,
      })
      assignDefaultMaterialToProperties({
        resource: resource,
        category: categoryItem,
      })
    }
    // 如果是动画分类，需要填充默认模型
    if (isCocosModelAnimationCategory(categoryItem)) {
      assignDefaultModelToProperties({
        modelResource: getAllModelResource(resource),
        category: categoryItem,
      })
    }

    // 如果是预设选项，并且没有 name 字段，需要补全
    if (
      isFePresetCategory(categoryItem) &&
      categoryItem.name === '' &&
      categoryItem.name_en === ''
    ) {
      categoryItem.name = categoryItem.prefix
      categoryItem.name_en = categoryItem.prefix
    }

    if (
      isElement(categoryItem.id) &&
      categoryItem &&
      categoryItem.sub_category
    ) {
      let elementStarsCategory = null
      // 收藏分类
      if (hasPermission({ key: 'category.star', permissionKeys })) {
        // 过滤元素的收藏分类并重新生成
        categoryItem.sub_category = categoryItem.sub_category.filter(
          (item) => !isElementStars(item.id),
        )
        elementStarsCategory = {
          ...cookElementStarsCategory({
            elementCategory: categoryItem,
            stars: starsConfig.element || [],
          }),
        }
      }

      elementStarsCategory &&
        categoryItem.sub_category.unshift(elementStarsCategory)
    }

    // 加工玩法的分类信息
    if (isGamePlay(categoryItem.id) && jsonConfig && categoryItem) {
      const hasSupportedMwConfigs =
        _.isArray(supportedMwConfigs) && !_.isEmpty(supportedMwConfigs)
      let customGameConfigCategory = []
      if (categoryItem.sub_category) {
        customGameConfigCategory = categoryItem.sub_category.filter((item) => {
          return !isGlobalCategory(item.id)
        })
      }
      // 清空然后重新生成 sub_category，一些需要用户可自定义的数据，如 title，desc 从 oldSubCategory 取出来
      categoryItem.sub_category = []
      // v1/v2 不展示游戏配置
      if (['v1', 'v2'].some((item) => item === gameDifficulty)) {
        categoryItem.sub_category.push({
          pid: categoryItem.id,
          id: `${gameDifficulty}_gameDifficulty`,
          prefix: `${gameDifficulty}_gameDifficulty`,
          name: 'Game Play',
          resource_type: gameConfigCategoryResourceType,
          priority: -2,
        })
      } else {
        // pt && dco不需要这几个分类信息
        if (hasSupportedMwConfigs) {
          if (hasTargetGameConfig('global_click')) {
            categoryItem.sub_category.push(
              cookGlobalClickCategory({ pid: categoryItem.id }),
            )
          }

          if (hasTargetGameConfig('auto_click')) {
            categoryItem.sub_category.push(
              cookAutoClickCategory({ pid: categoryItem.id }),
            )
          }

          if (hasTargetGameConfig('yd_click')) {
            categoryItem.sub_category.push(
              cookYdClickCategory({ pid: categoryItem.id }),
            )
          }
        }

        // autoEndTime 必须是数字、大于或等于 0
        if (hasTargetGameConfig('autoEndTime')) {
          if (jsonConfig.autoEndTime >= 0) {
            categoryItem.sub_category.push(
              cookAutoEndTimeCategory({ pid: categoryItem.id }),
            )
          } else {
            console.warn('autoEndTime 必须大于 0')
          }
        }
        // playAgain 必须是数字、大于 -1
        // 只有没有生成过玩法才会生成 playAgain 生成个之后，不再生成
        if (!hasInitPlayAgainCategory && hasTargetGameConfig('playAgain')) {
          if (jsonConfig.playAgain >= -1) {
            categoryItem.sub_category.push(
              cookPlayAgainCategory({ pid: categoryItem.id }),
            )
          } else {
            if (hasInitGameConfigCategory) {
              console.warn('已经生成过 playAgain')
            } else {
              console.warn('playAgain 必须大于或等于 -1')
            }
          }
        }
        // 对象且不为空
        if (hasTargetGameConfig('colorConfig')) {
          categoryItem.sub_category.push(
            cookColorConfigCategory({ pid: categoryItem.id }),
          )
        }

        // 加工游戏难度模式
        const gameDifficultyList = (gameConfigResource || {}).gameDifficultyList
        if (Array.isArray(gameDifficultyList)) {
          if (hasInitGameConfigCategory) {
            categoryItem.sub_category = [
              ...categoryItem.sub_category,
              ...customGameConfigCategory,
            ]
          } else {
            // 默认配置
            const gameConfig = gameDifficultyList.find(
              (item) => item.is_default === true,
            ).data

            _.each(gameConfig, (gameConfigItem, key) => {
              // 非挂靠在某个场景/资源/组别的配置，只有初始分类的时候才可以这样判断
              // 由于分类可以被任意调整，所以后续的分类的从属关系不能通过这种方式 resource、stage、group、scene 判断
              if (isGameConfigWithoutRelate(gameConfigItem)) {
                // 玩法配置 id，如果玩法有子分类，还需要将该 id 赋值给子分类的 pid
                const newGameConfigId = createFeGameConfigCategoryId()
                const prefix = key
                categoryItem.sub_category.push({
                  pid: categoryItem.id,
                  id: newGameConfigId,
                  prefix,
                  name:
                    getCommonGameConfigCategoryName(
                      playConfig.gameConfig[`[${prefix}]`],
                    ) ||
                    gameConfigItem.title ||
                    gameConfigItem.desc,
                  // 描述文案
                  desc:
                    getCommonGameConfigCategoryDesc(
                      playConfig.gameConfig[`[${prefix}]`],
                    ) ||
                    gameConfigItem.desc ||
                    gameConfigItem.title,
                  resource_type: gameConfigCategoryResourceType,
                  // 自定义 icon
                  icon: gameConfigItem.icon,
                  // 优先级
                  priority: gameConfigItem.priority,
                  sub_category: null,
                })
              }
            })
          }
        }

        if (!Array.isArray(gameDifficultyList)) {
          categoryItem.sub_category = [
            ...categoryItem.sub_category,
            ...customGameConfigCategory,
          ]
        }

        let gameConfigStarsCategory = null
        // 只有在有其他分类的时候才需要添加收藏分类
        // 多组合制作的玩法 tab 不需要收藏分类
        if (
          categoryItem.sub_category.length > 0 &&
          _.get(starsConfig, gameConfigCategoryResourceType) &&
          hasPermission({ key: 'category.star', permissionKeys })
        ) {
          // 过滤玩法的收藏分类，避免重复收藏
          categoryItem.sub_category = categoryItem.sub_category.filter(
            (item) => !isGameConfigStars(item.id),
          )
          gameConfigStarsCategory = {
            ...cookGameConfigStarsCategory({
              gameConfigCategory: categoryItem,
              stars: starsConfig.gameConfig || [],
            }),
          }
        }
        // 排序
        categoryItem.sub_category = _.sortBy(
          categoryItem.sub_category,
          (item) => item.priority,
        )

        // 通用模板制作页分类过滤
        if (isCommonTemplateCustomPage(page)) {
          // 当通用模板没有开放gameConfig.json配置时，过滤玩法分类，只展示全局跳转、自动跳转、诱导点击
          if (enableGameConfig !== 1) {
            categoryItem.sub_category = categoryItem.sub_category.filter(
              (item) =>
                [...commonTemplateCustomGameConfigCategory].some(
                  (j) => j === item.id,
                ),
            )
          }
        }

        gameConfigStarsCategory &&
          categoryItem.sub_category.unshift(gameConfigStarsCategory)
      }
    }

    // 图片语言包
    if (
      isImageLangPack({
        prefix: categoryItem.prefix,
        resourceType: categoryItem.resource_type,
      })
    ) {
      // 设置默认语言
      const list = useIdToFindResourceList({ resource, id: categoryItem.id })
      if (Array.isArray(list)) {
        commit('updateZipLangList', list)
        commit(
          'updatePreviewLanguageList',
          list.map((item) => item.zip_sign),
        )

        const currentLanguage = list.find((item) => item.is_default === 1)
        if (currentLanguage) {
          commit('updateCurrentLanguage', currentLanguage.zip_sign)
          commit('updatePreviewLanguage', currentLanguage.zip_sign)
        }
      }
    }

    if (Array.isArray(categoryItem.sub_category)) {
      // 如果是一个category组，sub_category里面包含game_config不允许隐藏
      categoryItem.hasGameConfigSub =
        categoryItem.sub_category.findIndex(
          (item) => item.resource_type === gameConfigCategoryResourceType,
        ) !== -1
      categoryItem.hasVideoSub =
        categoryItem.sub_category.findIndex(
          (item) => item.file_type === 'video',
        ) !== -1
      categoryItem.hasModelSub =
        categoryItem.sub_category.findIndex((item) =>
          isModelCategory(item.id, resource),
        ) !== -1
      categoryItem.sub_category = rebuildCategory({
        category: categoryItem.sub_category,
        hasInitGameConfigCategory,
        resource,
        jsonConfig,
        page,
        commit,
        playConfig,
        starsConfig,
        gameDifficulty,
        isMultiCustom,
        gameConfigResource,
        i18nResource,
        level: categoryItem.level + 1,
        mwConfig,
        supportedMwConfigs,
        enableGameConfig,
        permissionKeys,
      })
    }

    return categoryItem
  })

  return newCategory
}
